import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { matchesApi, sessionsApi, clientsApi, matchTxApi, sessionTxApi, positionsApi, dabbaApi } from '../../lib/api';
import { useSocketStore } from '../../store/socketStore';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { ClientTransactionsDialog } from '../shared/ClientTransactionsDialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { RefreshCw, AlertTriangle, Save, Pencil, Trash2, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Textarea } from '../ui/textarea';

export function TransactionsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscribe } = useSocketStore();
  
  // Check for view-only mode from URL params
  const viewOnly = searchParams.get('view_only') === 'true';
  const urlMatchId = searchParams.get('match_id') || '';
  const urlSessionId = searchParams.get('session_id') || '';
  const urlTab = searchParams.get('tab') || '';
  const fromPage = searchParams.get('from') || '';
  
  // Determine initial tab: URL tab param > session_id param > match
  const getInitialTab = () => {
    if (urlTab === 'session') return 'session';
    if (urlTab === 'match') return 'match';
    if (urlSessionId) return 'session';
    return 'match';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [matches, setMatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [matchTxns, setMatchTxns] = useState([]);
  const [sessionTxns, setSessionTxns] = useState([]);
  const [positions, setPositions] = useState([]);
  const [dabbaPositions, setDabbaPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(location.state?.matchId || urlMatchId || '');
  const [selectedSession, setSelectedSession] = useState(urlSessionId || '');
  const [limitExceeded, setLimitExceeded] = useState(null);
  const [clientTxDialog, setClientTxDialog] = useState({ open: false, clientId: null, clientName: null });
  const [showDeleted, setShowDeleted] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, txnId: null, type: null, reason: '' });

  // Handle back navigation for view-only mode
  const handleBackClick = () => {
    if (fromPage === 'settlement') {
      navigate('/settlement');
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  // Handle keyboard shortcuts for fast tab switching (F4/F5)
  // This allows staying on the same page and just switching tabs
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      // Handle F4 and F5 directly on this page without navigation
      if (e.key === 'F4') {
        e.preventDefault();
        setActiveTab('match');
      } else if (e.key === 'F5') {
        e.preventDefault();
        setActiveTab('session');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update tab when URL changes (for initial load or external navigation)
  React.useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'match' || tab === 'session') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Match Transaction Form State
  const [matchForm, setMatchForm] = useState({
    match_id: '',
    client_id: '',
    rate: '',
    amount: '',
    mode: 'K',
    team: ''
  });

  // Session Transaction Form State
  const [sessionForm, setSessionForm] = useState({
    session_id: '',
    client_id: '',
    rate: '',
    run: '',
    amount: '',
    mode: 'YES'
  });

  // Edit transaction state
  const [editMatchTxn, setEditMatchTxn] = useState(null);
  const [editSessionTxn, setEditSessionTxn] = useState(null);

  // Handle client name click
  const handleClientClick = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    setClientTxDialog({
      open: true,
      clientId,
      clientName: client?.name || clientId
    });
  };

  // Handle edit match transaction
  const handleEditMatchTxn = (txn) => {
    setEditMatchTxn({
      txn_id: txn.txn_id,
      team: txn.team,
      rate: txn.rate,
      amount: txn.amount,
      mode: txn.mode
    });
  };

  // Handle save match transaction edit
  const handleSaveMatchTxnEdit = async () => {
    try {
      await matchTxApi.update(editMatchTxn.txn_id, {
        team: editMatchTxn.team,
        rate: parseFloat(editMatchTxn.rate),
        amount: parseFloat(editMatchTxn.amount),
        mode: editMatchTxn.mode
      });
      toast.success('Transaction updated');
      setEditMatchTxn(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to update transaction');
    }
  };

  // Handle delete match transaction - opens dialog
  const handleDeleteMatchTxn = async (txnId, isPatti) => {
    if (isPatti) {
      toast.error('This is a Patti auto-generated transaction and cannot be deleted directly. Delete the source transaction instead.');
      return;
    }
    setDeleteDialog({ open: true, txnId, type: 'match', reason: '' });
  };

  // Confirm match delete
  const confirmDeleteMatchTxn = async () => {
    try {
      await matchTxApi.delete(deleteDialog.txnId, deleteDialog.reason);
      toast.success('Transaction deleted');
      setDeleteDialog({ open: false, txnId: null, type: null, reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete transaction');
    }
  };

  // Handle edit session transaction
  const handleEditSessionTxn = (txn) => {
    setEditSessionTxn({
      txn_id: txn.txn_id,
      runs: txn.runs,
      rate: txn.rate,
      amount: txn.amount,
      mode: txn.mode
    });
  };

  // Handle save session transaction edit
  const handleSaveSessionTxnEdit = async () => {
    try {
      await sessionTxApi.update(editSessionTxn.txn_id, {
        runs: parseInt(editSessionTxn.runs),
        rate: parseFloat(editSessionTxn.rate),
        amount: parseFloat(editSessionTxn.amount),
        mode: editSessionTxn.mode
      });
      toast.success('Transaction updated');
      setEditSessionTxn(null);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to update transaction');
    }
  };

  // Handle delete session transaction - opens dialog
  const handleDeleteSessionTxn = async (txnId, isPatti) => {
    if (isPatti) {
      toast.error('This is a Patti auto-generated transaction and cannot be deleted directly. Delete the source transaction instead.');
      return;
    }
    setDeleteDialog({ open: true, txnId, type: 'session', reason: '' });
  };

  // Confirm session delete
  const confirmDeleteSessionTxn = async () => {
    try {
      await sessionTxApi.delete(deleteDialog.txnId, deleteDialog.reason);
      toast.success('Transaction deleted');
      setDeleteDialog({ open: false, txnId: null, type: null, reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete transaction');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [matchesData, sessionsData, clientsData] = await Promise.all([
        matchesApi.list(),
        sessionsApi.list(),
        clientsApi.list()
      ]);
      setMatches(matchesData);
      setSessions(sessionsData);
      setClients(clientsData);

      // Fetch transactions if match is selected
      if (selectedMatch) {
        const [txns, pos, dabba] = await Promise.all([
          matchTxApi.list(selectedMatch),
          positionsApi.list(selectedMatch),
          dabbaApi.positions().catch(() => [])
        ]);
        setMatchTxns(txns);
        setPositions(pos);
        setDabbaPositions(dabba);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchTransactions = async (matchId) => {
    if (!matchId) return;
    try {
      const [txns, pos, dabba] = await Promise.all([
        matchTxApi.list(matchId, showDeleted),
        positionsApi.list(matchId),
        dabbaApi.positions().catch(() => [])
      ]);
      setMatchTxns(txns);
      setPositions(pos);
      setDabbaPositions(dabba);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSessionTransactions = async (sessionId) => {
    if (!sessionId) return;
    try {
      const txns = await sessionTxApi.list(sessionId, null, showDeleted);
      setSessionTxns(txns);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();

    const unsubs = [
      subscribe('TRANSACTION_CREATED', () => {
        if (selectedMatch) fetchMatchTransactions(selectedMatch);
        if (selectedSession) fetchSessionTransactions(selectedSession);
      }),
      subscribe('MATCH_DECLARED', fetchData),
      subscribe('SESSION_DECLARED', fetchData)
    ];

    return () => unsubs.forEach(u => u());
  }, [subscribe, selectedMatch, selectedSession]);

  // Refetch when showDeleted changes
  useEffect(() => {
    if (selectedMatch) fetchMatchTransactions(selectedMatch);
    if (selectedSession) fetchSessionTransactions(selectedSession);
  }, [showDeleted]);

  useEffect(() => {
    if (selectedMatch) {
      fetchMatchTransactions(selectedMatch);
      setMatchForm(f => ({ ...f, match_id: selectedMatch }));
    }
  }, [selectedMatch]);

  useEffect(() => {
    if (selectedSession) {
      fetchSessionTransactions(selectedSession);
      setSessionForm(f => ({ ...f, session_id: selectedSession }));
    }
  }, [selectedSession]);

  const handleMatchTxSubmit = async (override = false) => {
    try {
      const data = {
        match_id: matchForm.match_id,
        client_id: matchForm.client_id,
        rate: parseFloat(matchForm.rate),
        amount: parseFloat(matchForm.amount),
        mode: matchForm.mode,
        team: matchForm.team,
        override_limit: override
      };

      if (!data.match_id || !data.client_id || !data.rate || !data.amount || !data.team) {
        toast.error('All fields are required');
        return;
      }

      await matchTxApi.create(data);
      toast.success('Transaction created');
      
      // Reset form but keep match selected
      setMatchForm({
        match_id: matchForm.match_id,
        client_id: '',
        rate: '',
        amount: '',
        mode: 'K',
        team: ''
      });
      
      fetchMatchTransactions(matchForm.match_id);
    } catch (error) {
      console.log('Transaction error:', error, 'isAPIError:', error?.isAPIError, 'data:', error?.data);
      
      // Check if it's a structured limit exceeded error
      // error.data is the detail object from backend: {code, message, current_exposure, new_exposure, limit}
      if (error?.isAPIError && error?.data?.code === 'LIMIT_EXCEEDED') {
        setLimitExceeded(error.data);
      } else if (typeof error?.data === 'object' && error?.data?.code === 'LIMIT_EXCEEDED') {
        setLimitExceeded(error.data);
      } else if (error?.message?.includes('LIMIT_EXCEEDED')) {
        // Fallback: try to parse from message
        try {
          const parsed = JSON.parse(error.message);
          setLimitExceeded(parsed);
        } catch {
          toast.error(error?.message || 'Transaction failed');
        }
      } else {
        // Extract meaningful error message
        const msg = error?.data?.message || error?.message || 'Transaction failed';
        toast.error(msg);
      }
    }
  };

  const handleSessionTxSubmit = async () => {
    try {
      const data = {
        session_id: sessionForm.session_id,
        client_id: sessionForm.client_id,
        rate: parseFloat(sessionForm.rate),
        runs: parseInt(sessionForm.run),
        amount: parseFloat(sessionForm.amount),
        mode: sessionForm.mode
      };

      if (!data.session_id || !data.client_id || !data.rate || !data.runs || !data.amount) {
        toast.error('All fields are required');
        return;
      }

      await sessionTxApi.create(data);
      toast.success('Transaction created');
      
      // Reset form but keep session selected
      setSessionForm({
        session_id: sessionForm.session_id,
        client_id: '',
        rate: '',
        run: '',
        amount: '',
        mode: 'YES'
      });
      
      fetchSessionTransactions(sessionForm.session_id);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const selectedMatchData = matches.find(m => m.match_id === matchForm.match_id);
  const filteredSessions = sessions.filter(s => s.match_id === selectedMatch);

  // Get client name
  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.name || clientId?.slice(0, 8);
  };

  // Get client data for commission calculation
  const getClientData = (clientId) => {
    return clients.find(c => c.client_id === clientId) || {};
  };

  // Build patti relationship map from transactions (memoized)
  // Returns: { pattiClientId: { parentClientId, pattiPercent, pattiCommMode } }
  const pattiRelationships = React.useMemo(() => {
    const relationships = {};
    matchTxns.forEach(txn => {
      if (txn.is_patti && txn.parent_txn_id) {
        const parentTxn = matchTxns.find(t => t.txn_id === txn.parent_txn_id);
        if (parentTxn) {
          const pattiClientId = txn.client_id;
          const parentClientId = parentTxn.client_id;
          if (!relationships[pattiClientId]) {
            relationships[pattiClientId] = {
              parentClientId,
              pattiPercent: txn.patti_percent || 0,
              pattiCommMode: txn.patti_comm_mode || 'RECEIVE'
            };
          }
        }
      }
    });
    return relationships;
  }, [matchTxns]);

  // Calculate commission for a position value (team-wise calculation)
  // Commission is calculated on absolute position value and debited from that team's total
  const calculateCommission = (position, clientData) => {
    const userCommRate = (clientData.match_user_commission || 0) / 100;
    const refCommRate = (clientData.match_reference_commission || 0) / 100;
    const mode = clientData.match_commission_mode || 'PAY';
    
    let userComm = 0;
    let refComm = 0;
    
    // For PAY mode: commission when client is PLUS (position > 0)
    // For RECEIVE mode: commission when client is MINUS (position < 0)
    if (mode === 'PAY' && position > 0) {
      userComm = position * userCommRate;
      refComm = position * refCommRate;
    } else if (mode === 'RECEIVE' && position < 0) {
      userComm = Math.abs(position) * userCommRate;
      refComm = Math.abs(position) * refCommRate;
    }
    
    return { userComm, refComm };
  };

  // Calculate patti commission based on parent's commission (proportional)
  // For patti: commission = parent_commission * (patti_percent / 100)
  // Displayed on the same team as parent's positive position
  const calculatePattiCommission = (pattiClientId, positions) => {
    const relationship = pattiRelationships[pattiClientId];
    if (!relationship) return { teamA: { userComm: 0, refComm: 0 }, teamB: { userComm: 0, refComm: 0 } };
    
    const { parentClientId, pattiPercent } = relationship;
    const parentPosition = positions.find(p => p.client_id === parentClientId);
    if (!parentPosition) return { teamA: { userComm: 0, refComm: 0 }, teamB: { userComm: 0, refComm: 0 } };
    
    const parentClientData = getClientData(parentClientId);
    
    // Calculate parent's commission for each team
    const parentCommA = calculateCommission(parentPosition.team_a_position, parentClientData);
    const parentCommB = calculateCommission(parentPosition.team_b_position, parentClientData);
    
    // Patti commission = parent's commission * patti_percent / 100
    // Sign: We RECEIVE from patti (positive)
    return {
      teamA: {
        userComm: parentCommA.userComm * (pattiPercent / 100),
        refComm: parentCommA.refComm * (pattiPercent / 100)
      },
      teamB: {
        userComm: parentCommB.userComm * (pattiPercent / 100),
        refComm: parentCommB.refComm * (pattiPercent / 100)
      }
    };
  };

  // Helper to get teams array from match (supports both old and new formats)
  const getTeamsArray = (match) => {
    if (match?.teams && match.teams.length > 0) {
      return match.teams;
    }
    // Fallback for old format
    return [match?.team_a, match?.team_b].filter(t => t);
  };

  // Helper to get teams display string
  const getTeamsDisplay = (match) => {
    const teams = getTeamsArray(match);
    return teams.join(' vs ');
  };

  // Check if selected match has live commission enabled
  const currentMatchData = matches.find(m => m.match_id === selectedMatch);
  const isLiveCommission = currentMatchData?.live_commission !== false; // Default true

  // Calculate Main Book totals from positions (with or without commission based on setting)
  const mainBook = positions.reduce((acc, pos) => {
    const clientData = getClientData(pos.client_id);
    const isPatti = !!pattiRelationships[pos.client_id];
    let team_a_value = pos.team_a_position || 0;
    let team_b_value = pos.team_b_position || 0;
    
    if (isLiveCommission) {
      if (isPatti) {
        // Patti client: commission is proportional to parent's commission
        // We RECEIVE from patti, so commission adds to our book
        const pattiComm = calculatePattiCommission(pos.client_id, positions);
        team_a_value += pattiComm.teamA.userComm + pattiComm.teamA.refComm;
        team_b_value += pattiComm.teamB.userComm + pattiComm.teamB.refComm;
      } else {
        // Regular client: calculate commission normally
        const commA = calculateCommission(team_a_value, clientData);
        const commB = calculateCommission(team_b_value, clientData);
        
        // Both user commission and reference commission affect our book
        // When client is PLUS (we pay them), commission reduces what we pay = positive for us
        // When client is MINUS (they pay us), commission reduces what we receive = negative for us
        const mode = clientData.match_commission_mode || 'PAY';
        if (mode === 'PAY') {
          if (team_a_value > 0) {
            team_a_value -= commA.userComm;  // User comm: we pay less
            team_a_value -= commA.refComm;   // Ref comm: also deducted, goes to reference
          }
          if (team_b_value > 0) {
            team_b_value -= commB.userComm;
            team_b_value -= commB.refComm;
          }
        } else {
          if (team_a_value < 0) {
            team_a_value += commA.userComm;  // User comm: we receive more
            team_a_value += commA.refComm;   // Ref comm: also credited
          }
          if (team_b_value < 0) {
            team_b_value += commB.userComm;
            team_b_value += commB.refComm;
          }
        }
      }
    }
    
    acc.team_a += team_a_value;
    acc.team_b += team_b_value;
    return acc;
  }, { team_a: 0, team_b: 0 });

  // Calculate total commission for display (team-wise)
  const totalCommission = isLiveCommission ? positions.reduce((acc, pos) => {
    const isPatti = !!pattiRelationships[pos.client_id];
    
    if (isPatti) {
      // Patti: commission is proportional to parent's commission (we receive)
      const pattiComm = calculatePattiCommission(pos.client_id, positions);
      // Patti commission is received, so we subtract from totals (reduces our commission outflow)
      acc.teamA.userComm -= pattiComm.teamA.userComm;
      acc.teamA.refComm -= pattiComm.teamA.refComm;
      acc.teamB.userComm -= pattiComm.teamB.userComm;
      acc.teamB.refComm -= pattiComm.teamB.refComm;
    } else {
      // Regular client: add to commission totals (we pay)
      const clientData = getClientData(pos.client_id);
      const commA = calculateCommission(pos.team_a_position || 0, clientData);
      const commB = calculateCommission(pos.team_b_position || 0, clientData);
      acc.teamA.userComm += commA.userComm;
      acc.teamA.refComm += commA.refComm;
      acc.teamB.userComm += commB.userComm;
      acc.teamB.refComm += commB.refComm;
    }
    return acc;
  }, { 
    teamA: { userComm: 0, refComm: 0 }, 
    teamB: { userComm: 0, refComm: 0 } 
  }) : { 
    teamA: { userComm: 0, refComm: 0 }, 
    teamB: { userComm: 0, refComm: 0 } 
  };

  // Calculate Dabba Book totals
  const dabbaBook = dabbaPositions.reduce((acc, pos) => {
    acc.total += pos.total_dabba || 0;
    return acc;
  }, { total: 0 });

  // Get client commission rate for session
  const getClientSessionCommission = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    if (!client) return { rate: 0, mode: 'PAY' };
    return {
      rate: (client.session_user_commission || 0) / 100,
      mode: client.session_commission_mode || 'PAY',
      dabbaPercent: client.session_dabba_percent || 0
    };
  };

  // Calculate Run Meter for Session Transactions
  // Shows position at each run level - from CLIENT's perspective
  // YES at run X: + for runs >= X (client wins), - for runs < X (client loses)
  // NO at run X: + for runs < X (client wins), - for runs >= X (client loses)
  // Rate is direct multiplier: 1 = even money (win = amount), 0.9 = 90%, 1.5 = 150%
  const calculateSessionRunMeter = (transactions, isDabba = false) => {
    // Find min and max transaction runs to determine display range
    let minTxnRun = Infinity;
    let maxTxnRun = -Infinity;
    
    transactions.forEach(txn => {
      if (txn.is_patti) return;
      const txnRun = txn.runs || txn.run || 0;
      if (txnRun > 0) {
        minTxnRun = Math.min(minTxnRun, txnRun);
        maxTxnRun = Math.max(maxTxnRun, txnRun);
      }
    });
    
    // If no transactions, show runs 1-15
    if (minTxnRun === Infinity) {
      minTxnRun = 1;
      maxTxnRun = 1;
    }
    
    // Calculate display range: 5 predecessors + runs + 9 successors (15 total min)
    const displayStart = Math.max(1, minTxnRun - 5);
    const displayEnd = Math.max(displayStart + 14, maxTxnRun + 9);
    
    const runMeter = [];
    
    for (let run = displayStart; run <= displayEnd; run++) {
      let totalAmount = 0;
      let totalCommission = 0;
      
      transactions.forEach(txn => {
        if (txn.is_patti) return; // Skip patti transactions
        
        const txnRun = txn.runs || txn.run || 0;
        const amount = txn.amount || 0;
        const rate = txn.rate || 1; // Default to 1 (even money)
        const mode = txn.mode;
        
        // Get commission info for this client
        const commInfo = getClientSessionCommission(txn.client_id);
        const commRate = commInfo.rate;
        const commMode = commInfo.mode;
        const dabbaPercent = commInfo.dabbaPercent / 100;
        
        // Calculate position based on mode
        // Rate is direct multiplier: 1 = even money, 0.9 = 90%, 1.5 = 150%
        // Win amount = amount * rate
        // Loss amount = amount
        let positionAmount = 0;
        const winAmount = amount * rate;
        
        if (mode === 'YES') {
          if (run >= txnRun) {
            // YES wins - client gets win amount
            positionAmount = winAmount;
          } else {
            // YES loses - client loses stake amount
            positionAmount = -amount;
          }
        } else if (mode === 'NO') {
          if (run < txnRun) {
            // NO wins - client gets win amount
            positionAmount = winAmount;
          } else {
            // NO loses - client loses stake amount
            positionAmount = -amount;
          }
        }
        
        // Apply dabba percentage if this is dabba book
        if (isDabba) {
          positionAmount = positionAmount * dabbaPercent;
        }
        
        // Calculate commission on volume (amount is volume)
        // Commission is deducted regardless of win/loss
        if (commMode === 'PAY') {
          // We pay commission, so deduct from position
          totalCommission += amount * commRate;
        }
        
        totalAmount += positionAmount;
      });
      
      // Deduct commission from total
      const netAmount = totalAmount - totalCommission;
      
      runMeter.push({ 
        run, 
        amount: netAmount,
        grossAmount: totalAmount,
        commission: totalCommission
      });
    }
    
    return runMeter;
  };

  // Calculate main session book (non-dabba positions)
  const mainSessionRunMeter = calculateSessionRunMeter(sessionTxns, false);
  
  // Calculate dabba session book
  const dabbaSessionRunMeter = calculateSessionRunMeter(sessionTxns, true);

  const matchTxColumns = [
    { header: 'Time', render: (row) => row.created_at ? format(new Date(row.created_at), 'HH:mm:ss') : '-' },
    { header: 'Client', render: (row) => (
      <button 
        className="text-left text-primary hover:underline cursor-pointer font-medium"
        onClick={(e) => { e.stopPropagation(); handleClientClick(row.client_id); }}
      >
        {getClientName(row.client_id)}
      </button>
    )},
    { header: 'Type', render: (row) => row.is_patti ? (
      <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 font-medium">
        Patti {row.patti_percent ? `(${row.patti_percent}%)` : ''}
      </span>
    ) : null },
    { header: 'Mode', render: (row) => <StatusBadge status={row.mode} /> },
    { header: 'Team', accessor: 'team' },
    { header: 'Rate', accessor: 'rate', type: 'number' },
    { header: 'Amount', accessor: 'amount', type: 'number' },
    { header: 'Team A', accessor: 'team_a_impact', type: 'profit' },
    { header: 'Team B', accessor: 'team_b_impact', type: 'profit' },
    { 
      header: 'Actions', 
      render: (row) => {
        if (row.is_deleted) {
          return (
            <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium" data-testid="deleted-badge">
              Deleted
            </span>
          );
        }
        const match = matches.find(m => m.match_id === row.match_id);
        const isOpen = match?.status === 'OPEN';
        if (row.is_patti || row.transaction_origin === 'PATTI_AUTO') {
          return (
            <span className="text-xs text-muted-foreground cursor-help" title="Patti auto-generated transactions cannot be deleted directly">Auto</span>
          );
        }
        return isOpen ? (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditMatchTxn(row)} title="Edit" data-testid={`edit-match-txn-${row.txn_id}`}>
              <Pencil className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive hover:text-destructive" onClick={() => handleDeleteMatchTxn(row.txn_id, row.is_patti)} title="Delete" data-testid={`delete-match-txn-${row.txn_id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ) : null;
      }
    }
  ];

  const sessionTxColumns = [
    { header: 'Time', render: (row) => row.created_at ? format(new Date(row.created_at), 'HH:mm:ss') : '-' },
    { header: 'Client', render: (row) => (
      <button 
        className="text-left text-primary hover:underline cursor-pointer font-medium"
        onClick={(e) => { e.stopPropagation(); handleClientClick(row.client_id); }}
      >
        {getClientName(row.client_id)}
      </button>
    )},
    { header: 'Type', render: (row) => row.is_patti ? (
      <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 font-medium">
        Patti {row.patti_percent ? `(${row.patti_percent}%)` : ''}
      </span>
    ) : null },
    { header: 'Mode', render: (row) => <StatusBadge status={row.mode} /> },
    { header: 'Rate', accessor: 'rate', type: 'number' },
    { header: 'Run', accessor: 'runs', type: 'number' },
    { header: 'Amount', accessor: 'amount', type: 'number' },
    { header: 'Commission', accessor: 'session_commission', type: 'number' },
    { 
      header: 'Actions', 
      render: (row) => {
        if (row.is_deleted) {
          return (
            <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium" data-testid="session-deleted-badge">
              Deleted
            </span>
          );
        }
        const session = sessions.find(s => s.session_id === row.session_id);
        const isOpen = session?.status === 'OPEN';
        if (row.is_patti || row.transaction_origin === 'PATTI_AUTO') {
          return (
            <span className="text-xs text-muted-foreground cursor-help" title="Patti auto-generated transactions cannot be deleted directly">Auto</span>
          );
        }
        return isOpen ? (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditSessionTxn(row)} title="Edit" data-testid={`edit-session-txn-${row.txn_id}`}>
              <Pencil className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive hover:text-destructive" onClick={() => handleDeleteSessionTxn(row.txn_id, row.is_patti)} title="Delete" data-testid={`delete-session-txn-${row.txn_id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ) : null;
      }
    }
  ];

  // Get team names for position columns
  const matchTeams = getTeamsArray(selectedMatchData);
  
  const positionColumns = [
    { header: 'Client', render: (row) => (
      <button 
        className="text-left text-primary hover:underline cursor-pointer font-medium"
        onClick={(e) => { e.stopPropagation(); handleClientClick(row.client_id); }}
      >
        {getClientName(row.client_id)}
      </button>
    )},
    // Team positions (Nett Book)
    { 
      header: matchTeams[0] || 'Team A', 
      accessor: 'team_a_position', 
      type: 'profit',
      cellClassName: 'border-r border-border/30'
    },
    { 
      header: matchTeams[1] || 'Team B', 
      accessor: 'team_b_position', 
      type: 'profit',
      cellClassName: 'border-r border-border/50'
    },
    // User Commission columns - calculated when client is PLUS (PAY) or MINUS (RECEIVE)
    // For patti clients: commission is proportional to parent's commission
    { 
      header: `Comm ${matchTeams[0] || 'A'}`, 
      render: (row) => {
        const isPatti = !!pattiRelationships[row.client_id];
        
        if (isPatti) {
          // Patti client: commission = parent's commission * patti_percent / 100
          const pattiComm = calculatePattiCommission(row.client_id, positions);
          const userComm = pattiComm.teamA.userComm;
          if (userComm === 0) return <span className="text-muted-foreground">-</span>;
          // Patti commission is always positive (we RECEIVE from patti)
          return (
            <span className="text-green-400">
              +{userComm.toFixed(2)}
            </span>
          );
        } else {
          // Regular client: commission calculated on their own book
          const clientData = getClientData(row.client_id);
          const { userComm } = calculateCommission(row.team_a_position, clientData);
          if (userComm === 0) return <span className="text-muted-foreground">-</span>;
          const mode = clientData.match_commission_mode || 'PAY';
          const isDebit = mode === 'PAY' && row.team_a_position > 0;
          return (
            <span className={isDebit ? 'text-red-400' : 'text-green-400'}>
              {isDebit ? '-' : '+'}{userComm.toFixed(2)}
            </span>
          );
        }
      },
      type: 'number'
    },
    { 
      header: `Comm ${matchTeams[1] || 'B'}`, 
      render: (row) => {
        const isPatti = !!pattiRelationships[row.client_id];
        
        if (isPatti) {
          const pattiComm = calculatePattiCommission(row.client_id, positions);
          const userComm = pattiComm.teamB.userComm;
          if (userComm === 0) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-green-400">
              +{userComm.toFixed(2)}
            </span>
          );
        } else {
          const clientData = getClientData(row.client_id);
          const { userComm } = calculateCommission(row.team_b_position, clientData);
          if (userComm === 0) return <span className="text-muted-foreground">-</span>;
          const mode = clientData.match_commission_mode || 'PAY';
          const isDebit = mode === 'PAY' && row.team_b_position > 0;
          return (
            <span className={isDebit ? 'text-red-400' : 'text-green-400'}>
              {isDebit ? '-' : '+'}{userComm.toFixed(2)}
            </span>
          );
        }
      },
      type: 'number',
      cellClassName: 'border-r border-border/50'
    },
    // Reference Commission columns
    { 
      header: `Ref ${matchTeams[0] || 'A'}`, 
      render: (row) => {
        const isPatti = !!pattiRelationships[row.client_id];
        
        if (isPatti) {
          const pattiComm = calculatePattiCommission(row.client_id, positions);
          const refComm = pattiComm.teamA.refComm;
          if (refComm === 0) return <span className="text-muted-foreground">-</span>;
          // Patti ref commission is positive (we receive from patti)
          return <span className="text-green-400">+{refComm.toFixed(2)}</span>;
        } else {
          const clientData = getClientData(row.client_id);
          const { refComm } = calculateCommission(row.team_a_position, clientData);
          if (refComm === 0) return <span className="text-muted-foreground">-</span>;
          // Regular ref commission is negative (we pay to reference client)
          return <span className="text-red-400">-{refComm.toFixed(2)}</span>;
        }
      },
      type: 'number'
    },
    { 
      header: `Ref ${matchTeams[1] || 'B'}`, 
      render: (row) => {
        const isPatti = !!pattiRelationships[row.client_id];
        
        if (isPatti) {
          const pattiComm = calculatePattiCommission(row.client_id, positions);
          const refComm = pattiComm.teamB.refComm;
          if (refComm === 0) return <span className="text-muted-foreground">-</span>;
          return <span className="text-green-400">+{refComm.toFixed(2)}</span>;
        } else {
          const clientData = getClientData(row.client_id);
          const { refComm } = calculateCommission(row.team_b_position, clientData);
          if (refComm === 0) return <span className="text-muted-foreground">-</span>;
          return <span className="text-red-400">-{refComm.toFixed(2)}</span>;
        }
      },
      type: 'number'
    }
  ];

  return (
    <div className="space-y-3" data-testid="transactions-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold">
          Transactions
          {viewOnly && (
            <span className="ml-2 text-sm font-normal text-amber-500">(View Only)</span>
          )}
        </h1>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-transactions">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* View Only Banner with Back Button */}
      {viewOnly && (
        <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-amber-500">
              This match/session is declared. You are viewing in read-only mode. No changes can be made.
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleBackClick}
            className="gap-1 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
            data-testid="back-to-settlement"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Settlement
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="match" data-testid="tab-match-txn">Match Transaction</TabsTrigger>
          <TabsTrigger value="session" data-testid="tab-session-txn">Session Transaction</TabsTrigger>
        </TabsList>

        {/* Match Transaction Tab */}
        <TabsContent value="match" className="mt-3 space-y-3">
          {/* Match Selection */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label className="text-xs">Match Name</Label>
              <Select 
                value={selectedMatch || "none"} 
                onValueChange={(v) => !viewOnly && setSelectedMatch(v === "none" ? "" : v)}
                disabled={viewOnly}
              >
                <SelectTrigger className="h-8" data-testid="select-match">
                  <SelectValue placeholder="Select a match" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Match --</SelectItem>
                  {matches.filter(m => viewOnly ? true : m.status === 'OPEN').map(m => (
                    <SelectItem key={m.match_id} value={m.match_id}>
                      {m.name} - {getTeamsDisplay(m)} {m.status === 'DECLARED' ? '(Declared)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedMatch && selectedMatchData && (
              <div className="text-sm text-muted-foreground">
                {format(new Date(selectedMatchData.match_time), 'dd/MM/yyyy HH:mm')}
                {selectedMatchData.status === 'DECLARED' && (
                  <span className="ml-2 text-amber-500">• Winner: {selectedMatchData.winning_team}</span>
                )}
              </div>
            )}
          </div>

          {selectedMatch && (
            <>
              {/* Main Book & Dabba Book - Side by Side */}
              <div className="grid grid-cols-2 gap-3">
                <GroupBox 
                  label={
                    <div className="flex items-center gap-2">
                      <span>Main Book</span>
                      {isLiveCommission && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">LIVE COMM</span>
                      )}
                    </div>
                  } 
                  className="p-2"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">{matchTeams[0] || 'Team A'}</p>
                      <p className={`font-mono text-lg font-bold ${mainBook.team_a >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {mainBook.team_a.toFixed(2)}
                      </p>
                      {isLiveCommission && (totalCommission.teamA.userComm > 0 || totalCommission.teamA.refComm > 0) && (
                        <p className="text-[9px] text-muted-foreground mt-0.5">
                          {totalCommission.teamA.userComm > 0 && <span className="text-green-400">U:{totalCommission.teamA.userComm.toFixed(0)}</span>}
                          {totalCommission.teamA.refComm > 0 && <span className="text-amber-400 ml-1">R:{totalCommission.teamA.refComm.toFixed(0)}</span>}
                        </p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">{matchTeams[1] || 'Team B'}</p>
                      <p className={`font-mono text-lg font-bold ${mainBook.team_b >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {mainBook.team_b.toFixed(2)}
                      </p>
                      {isLiveCommission && (totalCommission.teamB.userComm > 0 || totalCommission.teamB.refComm > 0) && (
                        <p className="text-[9px] text-muted-foreground mt-0.5">
                          {totalCommission.teamB.userComm > 0 && <span className="text-green-400">U:{totalCommission.teamB.userComm.toFixed(0)}</span>}
                          {totalCommission.teamB.refComm > 0 && <span className="text-amber-400 ml-1">R:{totalCommission.teamB.refComm.toFixed(0)}</span>}
                        </p>
                      )}
                    </div>
                  </div>
                </GroupBox>

                <GroupBox label="Dabba Book" className="p-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Total Dabba</p>
                      <p className={`font-mono text-lg font-bold ${dabbaBook.total >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {dabbaBook.total.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Clients</p>
                      <p className="font-mono text-lg font-bold">
                        {dabbaPositions.length}
                      </p>
                    </div>
                  </div>
                </GroupBox>
              </div>

              {/* Entry Form - Hidden in View Only Mode */}
              {!viewOnly && (
              <GroupBox label="New Transaction" className="p-2">
                <div className="grid grid-cols-6 gap-2">
                  <div className="col-span-2">
                    <Label className="text-xs">Client *</Label>
                    <Select 
                      value={matchForm.client_id || "none"} 
                      onValueChange={(v) => setMatchForm({...matchForm, client_id: v === "none" ? "" : v})}
                    >
                      <SelectTrigger className="h-8" data-testid="match-tx-client">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Select --</SelectItem>
                        {clients.map(c => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Rate *</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={matchForm.rate} 
                      onChange={(e) => setMatchForm({...matchForm, rate: e.target.value})}
                      className="h-8"
                      data-testid="match-tx-rate"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Amount *</Label>
                    <Input 
                      type="number" 
                      value={matchForm.amount} 
                      onChange={(e) => setMatchForm({...matchForm, amount: e.target.value})}
                      className="h-8"
                      data-testid="match-tx-amount"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mode *</Label>
                    <Select 
                      value={matchForm.mode} 
                      onValueChange={(v) => setMatchForm({...matchForm, mode: v})}
                    >
                      <SelectTrigger className="h-8" data-testid="match-tx-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="K">K (Khaya)</SelectItem>
                        <SelectItem value="L">L (Lagaya)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Team *</Label>
                    <Select 
                      value={matchForm.team || "none"} 
                      onValueChange={(v) => setMatchForm({...matchForm, team: v === "none" ? "" : v})}
                    >
                      <SelectTrigger className="h-8" data-testid="match-tx-team">
                        <SelectValue placeholder="Team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Select --</SelectItem>
                        {selectedMatchData && getTeamsArray(selectedMatchData).map((team, index) => (
                          <SelectItem key={team} value={team}>{team}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <Button size="sm" onClick={() => handleMatchTxSubmit(false)} data-testid="save-match-tx">
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                </div>
              </GroupBox>
              )}

              {/* User Book (Positions) with Totals */}
              <GroupBox label="User Book">
                <div className="overflow-auto">
                  <table className="dense-table w-full">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th className="text-right">{matchTeams[0] || 'Team A'}</th>
                        <th className="text-right border-r border-border/30">{matchTeams[1] || 'Team B'}</th>
                        <th className="text-right">Comm {matchTeams[0] || 'A'}</th>
                        <th className="text-right border-r border-border/50">Comm {matchTeams[1] || 'B'}</th>
                        <th className="text-right">Ref {matchTeams[0] || 'A'}</th>
                        <th className="text-right">Ref {matchTeams[1] || 'B'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center text-muted-foreground py-8">
                            No positions yet
                          </td>
                        </tr>
                      ) : (
                        positions.map((row, idx) => {
                          const clientData = getClientData(row.client_id);
                          const isPatti = !!pattiRelationships[row.client_id];
                          
                          let commA, commB, isDebitA, isDebitB;
                          
                          if (isPatti) {
                            // Patti client: commission = parent's commission * patti_percent / 100
                            const pattiComm = calculatePattiCommission(row.client_id, positions);
                            commA = { userComm: pattiComm.teamA.userComm, refComm: pattiComm.teamA.refComm };
                            commB = { userComm: pattiComm.teamB.userComm, refComm: pattiComm.teamB.refComm };
                            // For patti: always show as positive (we receive from them)
                            isDebitA = false;
                            isDebitB = false;
                          } else {
                            // Regular client: calculate commission on their own book
                            commA = calculateCommission(row.team_a_position, clientData);
                            commB = calculateCommission(row.team_b_position, clientData);
                            const mode = clientData.match_commission_mode || 'PAY';
                            isDebitA = mode === 'PAY' && row.team_a_position > 0;
                            isDebitB = mode === 'PAY' && row.team_b_position > 0;
                          }
                          
                          return (
                            <tr key={row.position_id || idx}>
                              <td>
                                <button 
                                  className="text-left text-primary hover:underline cursor-pointer font-medium"
                                  onClick={(e) => { e.stopPropagation(); handleClientClick(row.client_id); }}
                                >
                                  {getClientName(row.client_id)}
                                </button>
                              </td>
                              <td className={`text-right ${(row.team_a_position || 0) >= 0 ? 'profit' : 'loss'}`}>
                                {(row.team_a_position || 0).toFixed(2)}
                              </td>
                              <td className={`text-right border-r border-border/30 ${(row.team_b_position || 0) >= 0 ? 'profit' : 'loss'}`}>
                                {(row.team_b_position || 0).toFixed(2)}
                              </td>
                              <td className="text-right">
                                {commA.userComm === 0 ? (
                                  <span className="text-muted-foreground">-</span>
                                ) : (
                                  <span className={isDebitA ? 'text-red-400' : 'text-green-400'}>
                                    {isDebitA ? '-' : '+'}{commA.userComm.toFixed(2)}
                                  </span>
                                )}
                              </td>
                              <td className="text-right border-r border-border/50">
                                {commB.userComm === 0 ? (
                                  <span className="text-muted-foreground">-</span>
                                ) : (
                                  <span className={isDebitB ? 'text-red-400' : 'text-green-400'}>
                                    {isDebitB ? '-' : '+'}{commB.userComm.toFixed(2)}
                                  </span>
                                )}
                              </td>
                              <td className="text-right">
                                {commA.refComm === 0 ? (
                                  <span className="text-muted-foreground">-</span>
                                ) : isPatti ? (
                                  <span className="text-green-400">+{commA.refComm.toFixed(2)}</span>
                                ) : (
                                  <span className="text-red-400">-{commA.refComm.toFixed(2)}</span>
                                )}
                              </td>
                              <td className="text-right">
                                {commB.refComm === 0 ? (
                                  <span className="text-muted-foreground">-</span>
                                ) : isPatti ? (
                                  <span className="text-green-400">+{commB.refComm.toFixed(2)}</span>
                                ) : (
                                  <span className="text-red-400">-{commB.refComm.toFixed(2)}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {positions.length > 0 && (
                      <tfoot className="bg-muted/50 font-semibold border-t-2 border-border">
                        <tr>
                          <td className="text-right">Total:</td>
                          <td className={`text-right ${positions.reduce((sum, r) => sum + (r.team_a_position || 0), 0) >= 0 ? 'profit' : 'loss'}`}>
                            {positions.reduce((sum, r) => sum + (r.team_a_position || 0), 0).toFixed(2)}
                          </td>
                          <td className={`text-right border-r border-border/30 ${positions.reduce((sum, r) => sum + (r.team_b_position || 0), 0) >= 0 ? 'profit' : 'loss'}`}>
                            {positions.reduce((sum, r) => sum + (r.team_b_position || 0), 0).toFixed(2)}
                          </td>
                          <td className="text-right">
                            {(() => {
                              // Calculate total user comm for Team A (accounting for patti)
                              const total = positions.reduce((sum, row) => {
                                const isPatti = !!pattiRelationships[row.client_id];
                                if (isPatti) {
                                  const pattiComm = calculatePattiCommission(row.client_id, positions);
                                  return sum + pattiComm.teamA.userComm; // Positive = we receive
                                } else {
                                  const clientData = getClientData(row.client_id);
                                  const comm = calculateCommission(row.team_a_position, clientData);
                                  return sum - comm.userComm; // Negative = we pay
                                }
                              }, 0);
                              if (total === 0) return <span className="text-muted-foreground">-</span>;
                              return <span className={total < 0 ? 'text-red-400' : 'text-green-400'}>{total.toFixed(2)}</span>;
                            })()}
                          </td>
                          <td className="text-right border-r border-border/50">
                            {(() => {
                              // Calculate total user comm for Team B (accounting for patti)
                              const total = positions.reduce((sum, row) => {
                                const isPatti = !!pattiRelationships[row.client_id];
                                if (isPatti) {
                                  const pattiComm = calculatePattiCommission(row.client_id, positions);
                                  return sum + pattiComm.teamB.userComm;
                                } else {
                                  const clientData = getClientData(row.client_id);
                                  const comm = calculateCommission(row.team_b_position, clientData);
                                  return sum - comm.userComm;
                                }
                              }, 0);
                              if (total === 0) return <span className="text-muted-foreground">-</span>;
                              return <span className={total < 0 ? 'text-red-400' : 'text-green-400'}>{total.toFixed(2)}</span>;
                            })()}
                          </td>
                          <td className="text-right">
                            {(() => {
                              // Calculate total ref comm for Team A (accounting for patti)
                              const total = positions.reduce((sum, row) => {
                                const isPatti = !!pattiRelationships[row.client_id];
                                if (isPatti) {
                                  const pattiComm = calculatePattiCommission(row.client_id, positions);
                                  return sum + pattiComm.teamA.refComm; // Positive = we receive
                                } else {
                                  const clientData = getClientData(row.client_id);
                                  const comm = calculateCommission(row.team_a_position, clientData);
                                  return sum - comm.refComm; // Negative = we pay
                                }
                              }, 0);
                              if (total === 0) return <span className="text-muted-foreground">-</span>;
                              return <span className={total < 0 ? 'text-red-400' : 'text-green-400'}>{total.toFixed(2)}</span>;
                            })()}
                          </td>
                          <td className="text-right">
                            {(() => {
                              // Calculate total ref comm for Team B (accounting for patti)
                              const total = positions.reduce((sum, row) => {
                                const isPatti = !!pattiRelationships[row.client_id];
                                if (isPatti) {
                                  const pattiComm = calculatePattiCommission(row.client_id, positions);
                                  return sum + pattiComm.teamB.refComm;
                                } else {
                                  const clientData = getClientData(row.client_id);
                                  const comm = calculateCommission(row.team_b_position, clientData);
                                  return sum - comm.refComm;
                                }
                              }, 0);
                              if (total === 0) return <span className="text-muted-foreground">-</span>;
                              return <span className={total < 0 ? 'text-red-400' : 'text-green-400'}>{total.toFixed(2)}</span>;
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </GroupBox>

              {/* Transactions Grid */}
              <GroupBox label={
                <div className="flex items-center justify-between w-full">
                  <span>Transactions</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-xs gap-1"
                    onClick={() => setShowDeleted(!showDeleted)}
                    data-testid="toggle-show-deleted"
                  >
                    {showDeleted ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
                  </Button>
                </div>
              }>
                <DenseTable 
                  columns={matchTxColumns} 
                  data={matchTxns}
                  emptyMessage="No transactions yet"
                  rowClassName={(row) => {
                    if (row.is_deleted) return 'opacity-50 bg-red-50 dark:bg-red-950/20 line-through';
                    if (row.is_patti) return 'bg-orange-50 dark:bg-orange-950/20 border-l-2 border-l-orange-400';
                    return '';
                  }}
                />
              </GroupBox>
            </>
          )}
        </TabsContent>

        {/* Session Transaction Tab */}
        <TabsContent value="session" className="mt-3 space-y-3">
          {/* Match & Session Selection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Match</Label>
              <Select 
                value={selectedMatch || "none"} 
                onValueChange={(v) => { if (!viewOnly) { setSelectedMatch(v === "none" ? "" : v); setSelectedSession(''); } }}
                disabled={viewOnly}
              >
                <SelectTrigger className="h-8" data-testid="session-select-match">
                  <SelectValue placeholder="Select a match" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select --</SelectItem>
                  {matches.filter(m => viewOnly ? true : m.status === 'OPEN').map(m => (
                    <SelectItem key={m.match_id} value={m.match_id}>{m.name} {m.status === 'DECLARED' ? '(Declared)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Session</Label>
              <Select 
                value={selectedSession || "none"} 
                onValueChange={(v) => !viewOnly && setSelectedSession(v === "none" ? "" : v)}
                disabled={!selectedMatch || viewOnly}
              >
                <SelectTrigger className="h-8" data-testid="session-select-session">
                  <SelectValue placeholder="Select a session" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select --</SelectItem>
                  {filteredSessions.filter(s => viewOnly ? true : s.status === 'OPEN').map(s => (
                    <SelectItem key={s.session_id} value={s.session_id}>{s.name} {s.status === 'DECLARED' ? `(Result: ${s.result})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedSession && (
            <>
              {/* Desktop Layout: Left Main Book | Center Content | Right Dabba Book */}
              <div className="grid grid-cols-12 gap-2">
                {/* Left - Main Session Book */}
                <div className="col-span-2">
                  <GroupBox label="Main Session Book" className="h-full">
                    <div className="overflow-auto" style={{ maxHeight: '330px' }}>
                      <table className="dense-table w-full">
                        <thead className="sticky top-0 bg-background z-10">
                          <tr>
                            <th className="text-center w-12 px-1">Run</th>
                            <th className="text-right px-1">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mainSessionRunMeter.map((item) => (
                            <tr key={item.run} className={item.amount > 0 ? 'bg-green-50 dark:bg-green-950/20' : item.amount < 0 ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                              <td className="text-center font-mono text-xs px-1">{item.run}</td>
                              <td className={`text-right font-mono text-xs px-1 ${item.amount > 0 ? 'text-green-600 font-medium' : item.amount < 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                                {item.amount >= 0 ? '+' : ''}{item.amount.toFixed(0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GroupBox>
                </div>

                {/* Center Content */}
                <div className="col-span-8 space-y-2">
                  {/* Entry Form - Hidden in View Only Mode */}
                  {!viewOnly && (
                  <GroupBox label="New Session Transaction" className="p-2">
                    <div className="grid grid-cols-5 gap-2">
                      <div>
                        <Label className="text-xs">Client *</Label>
                        <Select 
                          value={sessionForm.client_id || "none"} 
                          onValueChange={(v) => setSessionForm({...sessionForm, client_id: v === "none" ? "" : v})}
                        >
                          <SelectTrigger className="h-8" data-testid="session-tx-client">
                            <SelectValue placeholder="Client" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- Select --</SelectItem>
                            {clients.map(c => (
                              <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Rate *</Label>
                        <Input 
                          type="number" 
                          step="0.01"
                          value={sessionForm.rate} 
                          onChange={(e) => setSessionForm({...sessionForm, rate: e.target.value})}
                          className="h-8"
                          data-testid="session-tx-rate"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Run *</Label>
                        <Input 
                          type="number" 
                          value={sessionForm.run} 
                          onChange={(e) => setSessionForm({...sessionForm, run: e.target.value})}
                          className="h-8"
                          data-testid="session-tx-run"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Amount *</Label>
                        <Input 
                          type="number" 
                          value={sessionForm.amount} 
                          onChange={(e) => setSessionForm({...sessionForm, amount: e.target.value})}
                          className="h-8"
                          data-testid="session-tx-amount"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Mode *</Label>
                        <Select 
                          value={sessionForm.mode} 
                          onValueChange={(v) => setSessionForm({...sessionForm, mode: v})}
                        >
                          <SelectTrigger className="h-8" data-testid="session-tx-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="YES">YES</SelectItem>
                            <SelectItem value="NO">NO</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button size="sm" onClick={handleSessionTxSubmit} data-testid="save-session-tx">
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                    </div>
                  </GroupBox>
                  )}

                  {/* Transactions Grid */}
                  <GroupBox label={
                    <div className="flex items-center justify-between w-full">
                      <span>Session Transactions</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 text-xs gap-1"
                        onClick={() => setShowDeleted(!showDeleted)}
                        data-testid="toggle-show-deleted-session"
                      >
                        {showDeleted ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
                      </Button>
                    </div>
                  }>
                    <DenseTable 
                      columns={sessionTxColumns} 
                      data={sessionTxns}
                      emptyMessage="No transactions yet"
                      rowClassName={(row) => {
                        if (row.is_deleted) return 'opacity-50 bg-red-50 dark:bg-red-950/20 line-through';
                        if (row.is_patti) return 'bg-orange-50 dark:bg-orange-950/20 border-l-2 border-l-orange-400';
                        return '';
                      }}
                    />
                  </GroupBox>
                </div>

                {/* Right - Dabba Session Book */}
                <div className="col-span-2">
                  <GroupBox label="Dabba Book" className="h-full">
                    <div className="overflow-auto" style={{ maxHeight: '330px' }}>
                      <table className="dense-table w-full">
                        <thead className="sticky top-0 bg-background z-10">
                          <tr>
                            <th className="text-center w-12 px-1">Run</th>
                            <th className="text-right px-1">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dabbaSessionRunMeter.map((item) => (
                            <tr key={item.run} className={item.amount > 0 ? 'bg-green-50 dark:bg-green-950/20' : item.amount < 0 ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                              <td className="text-center font-mono text-xs px-1">{item.run}</td>
                              <td className={`text-right font-mono text-xs px-1 ${item.amount > 0 ? 'text-green-600 font-medium' : item.amount < 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                                {item.amount >= 0 ? '+' : ''}{item.amount.toFixed(0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GroupBox>
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Limit Exceeded Dialog */}
      <Dialog open={!!limitExceeded} onOpenChange={() => setLimitExceeded(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Client Limit Exceeded
            </DialogTitle>
            <DialogDescription>
              This transaction will exceed the client's exposure limit. Do you want to proceed?
            </DialogDescription>
          </DialogHeader>
          
          {limitExceeded && (
            <div className="space-y-3 py-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Current Exposure</p>
                  <p className="text-lg font-mono font-semibold">
                    ₹{limitExceeded.current_exposure?.toLocaleString() || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">New Exposure</p>
                  <p className="text-lg font-mono font-semibold text-amber-500">
                    +₹{limitExceeded.new_exposure?.toLocaleString() || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Client Limit</p>
                  <p className="text-lg font-mono font-semibold">
                    ₹{limitExceeded.limit?.toLocaleString() || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total After Txn</p>
                  <p className="text-lg font-mono font-semibold text-red-500">
                    ₹{((limitExceeded.current_exposure || 0) + (limitExceeded.new_exposure || 0)).toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                <p className="text-sm text-red-400">
                  Exceeds limit by ₹{((limitExceeded.current_exposure || 0) + (limitExceeded.new_exposure || 0) - (limitExceeded.limit || 0)).toLocaleString()}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLimitExceeded(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => { handleMatchTxSubmit(true); setLimitExceeded(null); }}
              data-testid="allow-limit-override"
            >
              Override & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Transactions Dialog */}
      <ClientTransactionsDialog
        open={clientTxDialog.open}
        onClose={() => setClientTxDialog({ open: false, clientId: null, clientName: null })}
        clientId={clientTxDialog.clientId}
        clientName={clientTxDialog.clientName}
        matchId={selectedMatch || null}
      />

      {/* Edit Match Transaction Dialog */}
      <Dialog open={!!editMatchTxn} onOpenChange={() => setEditMatchTxn(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Match Transaction</DialogTitle>
          </DialogHeader>
          {editMatchTxn && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Team</Label>
                  <Select value={editMatchTxn.team} onValueChange={(v) => setEditMatchTxn({...editMatchTxn, team: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getTeamsArray(selectedMatchData).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={editMatchTxn.mode} onValueChange={(v) => setEditMatchTxn({...editMatchTxn, mode: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="K">K (Khaya)</SelectItem>
                      <SelectItem value="L">L (Lagaya)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rate</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={editMatchTxn.rate} 
                    onChange={(e) => setEditMatchTxn({...editMatchTxn, rate: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Amount</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={editMatchTxn.amount} 
                    onChange={(e) => setEditMatchTxn({...editMatchTxn, amount: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMatchTxn(null)}>Cancel</Button>
            <Button onClick={handleSaveMatchTxnEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, txnId: null, type: null, reason: '' })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Transaction
            </DialogTitle>
            <DialogDescription>
              This transaction will be soft-deleted. It will remain visible but excluded from all calculations, books, and reports. Any linked Patti transactions will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea
                placeholder="Enter reason for deletion..."
                value={deleteDialog.reason}
                onChange={(e) => setDeleteDialog({ ...deleteDialog, reason: e.target.value })}
                className="mt-1"
                rows={2}
                data-testid="delete-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, txnId: null, type: null, reason: '' })} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => deleteDialog.type === 'match' ? confirmDeleteMatchTxn() : confirmDeleteSessionTxn()}
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Session Transaction Dialog */}
      <Dialog open={!!editSessionTxn} onOpenChange={() => setEditSessionTxn(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Session Transaction</DialogTitle>
          </DialogHeader>
          {editSessionTxn && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Mode</Label>
                  <Select value={editSessionTxn.mode} onValueChange={(v) => setEditSessionTxn({...editSessionTxn, mode: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="YES">YES</SelectItem>
                      <SelectItem value="NO">NO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Run</Label>
                  <Input 
                    type="number" 
                    value={editSessionTxn.runs} 
                    onChange={(e) => setEditSessionTxn({...editSessionTxn, runs: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rate</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={editSessionTxn.rate} 
                    onChange={(e) => setEditSessionTxn({...editSessionTxn, rate: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Amount</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={editSessionTxn.amount} 
                    onChange={(e) => setEditSessionTxn({...editSessionTxn, amount: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSessionTxn(null)}>Cancel</Button>
            <Button onClick={handleSaveSessionTxnEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
